/**
 * Layer-2 (Zod fetch-boundary) STRUCTURAL GUARDS for the reviews app.
 *
 * Response-shape parity campaign — Phase 6b Layer-2 adoption (structural-guard
 * posture). Plan:
 * agents/plans/response_shape_fleet_rollout_plan_2026-06-09.md
 *
 * These guards are passed to `useApiQuery`'s `schema:` option (via
 * {@link asResponseGuard}) so the unwrapped, post-transform payload is validated
 * at the network boundary. A failure throws an attributable
 * `ResponseValidationError` carrying the request URL instead of surfacing as a
 * `Cannot read properties of undefined` crash deep in a component render.
 *
 * POSTURE — DELIBERATELY MAXIMALLY-LENIENT STRUCTURAL GUARD (NOT a full
 * contracts schema):
 *   - Every reviews endpoint returns `ApiResponse[<XxxData>]`; `useApiQuery`
 *     unwraps the `{ success, data }` envelope, so the guarded payload is always
 *     an OBJECT (never a bare array) — the list endpoints wrap their arrays in a
 *     `{ items, pagination|total }` container.
 *   - The envelope's `data` field is declared `T | null` in the live OpenAPI
 *     spec, so the unwrapped payload may legitimately be `null`. The guard is
 *     therefore `.nullable()` — it CANNOT over-reject any response the UI
 *     renders today.
 *   - No field is asserted. The guard catches ONLY gross structural drift: a
 *     bare-array payload where an object is expected, a primitive, or an
 *     un-unwrapped value — the silent-empty bug class from the 2026-04-29 CRM
 *     incident.
 *
 * SHAPE-DIVERGENCE NOTE (for a future Phase 6a / full-strictness upgrade):
 *   The committed `types.gen.ts` (and the static `<T>` generics in queries.ts)
 *   describe the OLD flat envelope `{ id, success, data: Item[], pagination }`,
 *   but the LIVE server (verified 2026-06-11 via `/openapi.json`) returns the
 *   nested `ApiResponse[<XxxListData>]` = `{ success, data: { items, pagination }
 *   }`. Several route components still read `.data` where the unwrapped payload
 *   now carries `.items`. That Layer-1 codegen drift is OUT OF SCOPE for this
 *   6b session (owned by manager-reviews / Phase 6a) — it is the very reason the
 *   guard stays maximally lenient: tightening any of these to a faithful
 *   `@plerity/contracts` object schema MUST first re-capture the live response
 *   and confirm `.parse()` succeeds (plan "Two adoption postures").
 *
 * The static `<T>` on each `useApiQuery` call stays owned by the hand/generated
 * type in `../types/reviews` (Layer 1). {@link asResponseGuard} is the
 * deliberate seam casting these runtime guards to that static contract.
 */

import { z } from "zod";

/**
 * Cast a structural Zod guard to the hook's `ResponseValidator<T>` seam.
 *
 * Layer 1 (the hand-written / OpenAPI-generated `<T>`) owns the STATIC type;
 * this guard owns RUNTIME structure only. The lenient guard's inferred output
 * intentionally differs from `<T>`, so the cast bridges the two layers without
 * weakening the caller's static type.
 */
export function asResponseGuard<T>(schema: { parse(data: unknown): unknown }): {
  parse(data: unknown): T;
} {
  return schema as { parse(data: unknown): T };
}

/**
 * Maximally-lenient structural guard: an object (any keys preserved) or `null`.
 * One factory per response shape so a future per-endpoint tightening (after a
 * live-response capture) edits a single line.
 */
function lenientObjectGuard() {
  return z.object({}).passthrough().nullable();
}

// ---------------------------------------------------------------------------
// Reviews
// ---------------------------------------------------------------------------

/** GET /api/reviews → ApiResponse[ReviewListData] → { items, pagination } */
export const ReviewsListGuard = lenientObjectGuard();
/** GET /api/reviews/{id} → ApiResponse[ReviewListItem] → object */
export const ReviewGuard = lenientObjectGuard();
/** GET /api/reviews/stats → ApiResponse[ReviewStatsData] → object */
export const ReviewStatsGuard = lenientObjectGuard();
/** GET /api/reviews/trends → ApiResponse[ReviewTrendsData] → { items } */
export const ReviewTrendsGuard = lenientObjectGuard();

// ---------------------------------------------------------------------------
// Campaigns
// ---------------------------------------------------------------------------

/** GET /api/campaigns → ApiResponse[CampaignListData] → { items, total } */
export const CampaignsGuard = lenientObjectGuard();
/** GET /api/campaigns/{id} → ApiResponse[CampaignItem] → object */
export const CampaignGuard = lenientObjectGuard();

// ---------------------------------------------------------------------------
// Competitors
// ---------------------------------------------------------------------------

/** GET /api/competitors → ApiResponse[CompetitorListData] → { items, total } */
export const CompetitorsGuard = lenientObjectGuard();
/** GET /api/competitors/comparison → ApiResponse[CompetitorComparisonData] → object */
export const CompetitorComparisonGuard = lenientObjectGuard();

// ---------------------------------------------------------------------------
// Alerts
// ---------------------------------------------------------------------------

/** GET /api/alerts → ApiResponse[AlertListData] → { items, total } */
export const AlertsGuard = lenientObjectGuard();

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

/** GET /api/settings → ApiResponse[SettingsData] → object */
export const ReviewSettingsGuard = lenientObjectGuard();

// ---------------------------------------------------------------------------
// Locations
// ---------------------------------------------------------------------------

/** GET /api/locations → ApiResponse[LocationListData] → { items, total } */
export const LocationsGuard = lenientObjectGuard();

/** GET /api/businesses → ApiResponse[BusinessListData] → { items, total } */
export const BusinessesGuard = lenientObjectGuard();
